/**
 * §21.4 — the signed round token.
 *
 * Issued to each player when a question opens. The answer function verifies the
 * signature without a preliminary lookup, which is what keeps the cloud answer
 * path to one short Neon insert (§21.3, §23.2). The database unique constraint
 * on (question_round_id, session_player_id) remains the final idempotency guard
 * (§21.4, §30.1).
 */

export interface RoundTokenClaims {
  sessionId: string;
  roundId: string;
  sessionPlayerId: string;
  /** Epoch ms — end of the reading window, start of the scored countdown (§8.3). */
  opensAt: number;
  closesAt: number;
  nonce: string;
}

const encoder = new TextEncoder();

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(input: string): Uint8Array<ArrayBuffer> {
  const padded = input
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(input.length / 4) * 4, "=");
  const binary = atob(padded);
  // Allocated over an explicit ArrayBuffer so it satisfies BufferSource, which
  // excludes SharedArrayBuffer-backed views.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function importKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

export async function signRoundToken(claims: RoundTokenClaims, secret: string): Promise<string> {
  const body = base64UrlEncode(encoder.encode(JSON.stringify(claims)));
  const key = await importKey(secret);
  const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(body));
  return `${body}.${base64UrlEncode(new Uint8Array(signature))}`;
}

export async function verifyRoundToken(
  token: string,
  secret: string,
): Promise<RoundTokenClaims | null> {
  const separator = token.lastIndexOf(".");
  if (separator <= 0) return null;

  const body = token.slice(0, separator);
  const signature = token.slice(separator + 1);

  const key = await importKey(secret);
  let valid: boolean;
  try {
    valid = await crypto.subtle.verify(
      "HMAC",
      key,
      base64UrlDecode(signature),
      encoder.encode(body),
    );
  } catch {
    return null;
  }
  if (!valid) return null;

  try {
    const claims = JSON.parse(new TextDecoder().decode(base64UrlDecode(body))) as RoundTokenClaims;
    if (
      typeof claims.sessionId !== "string" ||
      typeof claims.roundId !== "string" ||
      typeof claims.sessionPlayerId !== "string" ||
      typeof claims.opensAt !== "number" ||
      typeof claims.closesAt !== "number"
    ) {
      return null;
    }
    return claims;
  } catch {
    return null;
  }
}

/** Opaque bearer token for a player's session identity, signed the same way. */
export interface PlayerTokenClaims {
  tenantId: string;
  playerProfileId: string;
  issuedAt: number;
  expiresAt: number;
}

export async function signPlayerToken(claims: PlayerTokenClaims, secret: string): Promise<string> {
  return signRoundToken(claims as unknown as RoundTokenClaims, secret);
}

export async function verifyPlayerToken(
  token: string,
  secret: string,
  now = Date.now(),
): Promise<PlayerTokenClaims | null> {
  const separator = token.lastIndexOf(".");
  if (separator <= 0) return null;

  const body = token.slice(0, separator);
  const key = await importKey(secret);
  let valid: boolean;
  try {
    valid = await crypto.subtle.verify(
      "HMAC",
      key,
      base64UrlDecode(token.slice(separator + 1)),
      encoder.encode(body),
    );
  } catch {
    return null;
  }
  if (!valid) return null;

  try {
    const claims = JSON.parse(new TextDecoder().decode(base64UrlDecode(body))) as PlayerTokenClaims;
    if (typeof claims.playerProfileId !== "string" || typeof claims.expiresAt !== "number")
      return null;
    if (claims.expiresAt < now) return null;
    return claims;
  } catch {
    return null;
  }
}
